import { ScrollArea } from '@heidi/design-system/scroll-area';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight, Search } from 'lucide-react';
import { useId, useState, type JSX } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type { ToolUseItem } from '../../../../../contracts/harness.types';
import { workingBlockMessages } from '../../../../../data/projects/timeline/working-block/workingBlock.messages';
import { inlineToolCallLabel } from '../../inlineToolCallLabel';
import { ShimmerText } from '../../ShimmerText';
import { toolIcon } from '../../toolPresentation';
import { useWorkingRowText } from '../../useWorkingRowText';
import { WORKING_TOGGLE_PRESS, WorkingDisclosure } from '../../WorkingDisclosure';
import { EvidenceBadge } from './EvidenceBadge';
import { SourceAvatars, SourceCycle, SourceGlyph } from './EvidenceSources';
import {
  sourceDomain,
  sourceKey,
  type EvidenceSearch,
  type EvidenceSource,
  type EvidenceTrails,
} from './evidenceTrail';
import { useEvidenceTrail } from './useEvidenceTrail';

const TOGGLE = cn(
  'flex w-full min-w-0 items-center gap-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
  WORKING_TOGGLE_PRESS
);

/** `reveal` is the owning button's hover/focus classes, spelled out so Tailwind sees them. */
const Chevron = ({ open, reveal }: { open: boolean; reveal: string }) => (
  <ChevronRight
    className={cn(
      'size-3.5 shrink-0 text-text-tertiary opacity-60 rtl:-scale-x-100',
      'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out-quart motion-reduce:transition-none',
      reveal,
      open && 'rotate-90'
    )}
    aria-hidden
  />
);

/** Rows inside an opened step: flush with it, at the Working list's spacing. */
const ROWS = 'flex flex-col gap-3';

/** A step and its sources row: closer than the list's rows, so the row reads as the step's. */
const STEP = 'flex w-full min-w-0 flex-col gap-2';

const SOURCE_LINE = 'flex w-full min-w-0 items-center gap-2';

/**
 * Logo, title, then where it is from, as Evidence's own source list lays them
 * out: the publisher for a web source, "Your library" for one from the clinician's library.
 */
const SourceName = ({ source }: { source: EvidenceSource }) => {
  const intl = useIntl();
  const rowText = useWorkingRowText();
  const domain = sourceDomain(source);
  const origin = source.fromLibrary
    ? intl.formatMessage(workingBlockMessages.evidenceYourLibrary)
    : source.title && domain;
  return (
    <>
      <SourceGlyph source={source} />
      <TypographyP2 className={cn(rowText, 'w-0 flex-1 truncate text-text-secondary')}>
        {source.title || domain || source.url}
      </TypographyP2>
      {origin ? (
        <TypographyP2 className={cn(rowText, 'truncate text-text-tertiary')}>
          {origin}
        </TypographyP2>
      ) : null}
    </>
  );
};

/** "7 sources found", with any from the clinician's library counted apart, as the Evidence card splits them. */
const sourcesFoundLabel = (
  intl: IntlShape,
  sources: readonly EvidenceSource[]
): string => {
  const library = sources.filter((source) => source.fromLibrary).length;
  const count = sources.length - library;
  if (!library) {
    return intl.formatMessage(workingBlockMessages.evidenceSourcesFound, { count });
  }
  if (!count) {
    return intl.formatMessage(workingBlockMessages.evidenceLibrarySourcesFound, {
      count: library,
    });
  }
  return intl.formatMessage(workingBlockMessages.evidenceSourcesFoundWithLibrary, {
    count,
    library,
  });
};

/** One search: what it asked, shimmering until it returns while the step still runs. */
const SearchRow = ({ search, running }: { search: EvidenceSearch; running: boolean }) => {
  const rowText = useWorkingRowText();
  return (
    <div className="flex min-w-0 items-center gap-2" data-testid="evidence-search-row">
      <Search className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
      <TypographyP2 className={cn(rowText, 'text-text-tertiary')}>
        {running && !search.done ? (
          <ShimmerText className="inline">{search.query}</ShimmerText>
        ) : (
          search.query
        )}
      </TypographyP2>
    </div>
  );
};

export interface EvidenceSourcesRowProps {
  sources: readonly EvidenceSource[];
  /** Opens one source; the app routes it and reports the click. Without it the list only names them. */
  onOpenSource?: (source: EvidenceSource, citationNumber: number) => void;
}

/**
 * "N sources found", a row of its own beneath the step: the count and the top
 * publishers, opening onto the sources by name. It is the answer's one route
 * to its sources (spec v3 §4).
 */
export const EvidenceSourcesRow = ({
  sources,
  onOpenSource,
}: EvidenceSourcesRowProps) => {
  const intl = useIntl();
  const rowText = useWorkingRowText();
  const [open, setOpen] = useState(false);
  const listId = useId();
  return (
    <div className="flex min-w-0 flex-col" data-testid="evidence-sources-row">
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        aria-controls={listId}
        className={cn('group/evidence-sources', TOGGLE)}
      >
        <SourceAvatars sources={sources} />
        <TypographyP2 className={cn(rowText, 'text-text-tertiary')}>
          {sourcesFoundLabel(intl, sources)}
        </TypographyP2>
        <Chevron
          open={open}
          reveal="group-hover/evidence-sources:opacity-100 group-focus-visible/evidence-sources:opacity-100"
        />
      </button>
      <WorkingDisclosure id={listId} open={open} className="pt-2">
        {/* Capped at the height the Evidence card's own source list scrolls past. */}
        <ScrollArea
          scrollbarVariant="minimal"
          className="max-h-64"
          viewportClassName="max-h-64"
          data-testid="evidence-sources-list"
        >
          <ul className={ROWS}>
            {sources.map((source, index) => (
              <li key={sourceKey(source)}>
                {onOpenSource ? (
                  <button
                    type="button"
                    onClick={() => onOpenSource(source, index + 1)}
                    className={cn(
                      SOURCE_LINE,
                      'rounded-sm text-start hover:bg-surface-hover',
                      focusRing
                    )}
                  >
                    <SourceName source={source} />
                  </button>
                ) : (
                  <div className={SOURCE_LINE}>
                    <SourceName source={source} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </ScrollArea>
      </WorkingDisclosure>
    </div>
  );
};

export interface EvidenceWorkRowProps {
  item: ToolUseItem;
  /** Where the step's trail is read. */
  trails: EvidenceTrails | undefined;
}

/**
 * An Evidence step inside the Working block: its label with the Evidence badge
 * and, while it runs, the publishers it has found so far and how many searches
 * it has launched. Opened, it lists the searches; once complete, the sources
 * they found sit beneath it on their own row.
 */
export const EvidenceWorkRow = ({ item, trails }: EvidenceWorkRowProps): JSX.Element => {
  const intl = useIntl();
  const rowText = useWorkingRowText();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const running = item.status !== 'complete';
  const stepTrail = useEvidenceTrail(trails, item);
  const searches = stepTrail?.searches ?? [];
  const found = stepTrail?.found?.length ? stepTrail.found : null;
  const Icon = toolIcon(item.name);
  const label = inlineToolCallLabel(intl, {
    toolName: item.name,
    status: running ? 'running' : 'success',
    input: item.input,
    toolCallDisplay: item.toolCallDisplay,
  });

  const header = (
    <>
      <Icon className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
      <TypographyP2 className={cn(rowText, 'text-text-tertiary')}>
        {running ? <ShimmerText className="inline">{label}</ShimmerText> : label}
        {running && !open && searches.length > 0 ? (
          <span>{` · ${intl.formatMessage(workingBlockMessages.evidenceSearches, {
            count: searches.length,
          })}`}</span>
        ) : null}
      </TypographyP2>
      <EvidenceBadge />
      {running && stepTrail ? <SourceCycle sources={stepTrail.returned} /> : null}
    </>
  );

  const openSource = trails?.openSource;
  const sourcesRow = found ? (
    <EvidenceSourcesRow
      sources={found}
      onOpenSource={
        openSource
          ? (source, citationNumber) => openSource(item, source, citationNumber)
          : undefined
      }
    />
  ) : null;

  if (searches.length === 0) {
    return (
      <div className={STEP} data-testid="evidence-work-row">
        <div className="flex w-full min-w-0 items-center gap-2">{header}</div>
        {sourcesRow}
      </div>
    );
  }
  return (
    <div className={STEP} data-testid="evidence-work-row">
      <div className="flex w-full min-w-0 flex-col">
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-controls={listId}
          className={cn('group/evidence-row', TOGGLE)}
        >
          {header}
          <Chevron
            open={open}
            reveal="group-hover/evidence-row:opacity-100 group-focus-visible/evidence-row:opacity-100"
          />
        </button>
        <WorkingDisclosure id={listId} open={open} className="pt-2">
          <ul className={ROWS}>
            {searches.map((search, index) => (
              // A query can repeat within one step; its position cannot.
              <li key={`${index}-${search.query}`}>
                <SearchRow search={search} running={running} />
              </li>
            ))}
          </ul>
        </WorkingDisclosure>
      </div>
      {sourcesRow}
    </div>
  );
};
